import { FC } from 'react';

import { getVoorraadSeverity, VoorraadSeverityVariant } from './voorraadSeverity';

import styles from './VoorraadDots.module.css';

const DOT_COUNT = 3;

const VARIANT_LABEL: Record<VoorraadSeverityVariant, string> = {
  success: 'voldoende',
  warning: 'matig tekort',
  error: 'ernstig tekort',
};

/** How many of the 3 dots are shown as 'filled' (solid); the rest are 'faded' (subtle). */
const FILLED_DOT_COUNT_BY_VARIANT: Record<VoorraadSeverityVariant, number> = {
  success: 3,
  warning: 2,
  error: 1,
};

/**
 * Combined 'over/tekort' indicator: the number itself, plus a 3-dot severity indicator next to
 * it. All 3 dots filled (green) for a surplus, 2 filled (orange) for a moderate shortage, 1
 * filled (red) for a severe shortage. The remaining dots are shown 'faded' rather than hidden,
 * so the total of 3 is always visible.
 */
const VoorraadDots: FC<{
  aantal: number;
  claimsCount?: number;
  /** Getoond ipv `aantal` bij de oranje (matig tekort) categorie: het over/tekort dat opgelost kan worden met claims. */
  overOfTekortMetClaims?: number;
  /** Getoond ipv `aantal` bij de rode (ernstig tekort) categorie. */
  beschikbareReizigersdienst?: number;
  nvt?: boolean;
  compact?: boolean;
}> = ({
  aantal,
  claimsCount = 0,
  overOfTekortMetClaims = aantal,
  beschikbareReizigersdienst = aantal,
  nvt = false,
  compact = false,
}) => {
  const variant = getVoorraadSeverity(aantal, claimsCount);
  const filledCount = FILLED_DOT_COUNT_BY_VARIANT[variant];
  const displayAantalByVariant: Record<VoorraadSeverityVariant, number> = {
    success: aantal,
    warning: overOfTekortMetClaims,
    error: beschikbareReizigersdienst,
  };
  const displayAantal = displayAantalByVariant[variant];
  const isNvt = nvt || Number.isNaN(displayAantal);
  const label = isNvt ? 'Over/tekort: nvt' : `Over/tekort: ${displayAantal} (${VARIANT_LABEL[variant]})`;

  return (
    <div className={[styles.voorraadDots, compact ? styles.compact : ''].join(' ')} role='img' aria-label={label}>
      <span className={styles.aantal} aria-hidden='true'>
        {isNvt ? 'nvt' : displayAantal}
      </span>
      <div className={styles.dotsGroup} aria-hidden='true'>
        {!isNvt &&
          Array.from({ length: DOT_COUNT }, (_, index) => (
            <span
              key={index}
              className={[styles.dot, styles[variant], index < filledCount ? styles.filled : styles.faded].join(' ')}
            />
          ))}
      </div>
    </div>
  );
};
export default VoorraadDots;
